import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Specificity = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🎯 CSS Specificity</h1>

      <p className="tutorial-intro">
        <b>CSS Specificity</b> determines which style rule is applied when
        multiple rules target the same element. It’s a ranking system for CSS
        selectors. The more specific the selector, the higher priority it has.
      </p>

      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e", marginBottom: "0.5rem" }}>
          🔑 Specificity Order
        </h2>
        <ul
          style={{
            lineHeight: "1.8",
            fontSize: "1.05rem",
            paddingLeft: "25px",
          }}
        >
          <li>
            <b>Inline styles</b> (highest priority)
          </li>
          <li>
            <b>ID selectors</b> (#id)
          </li>
          <li>
            <b>Class, pseudo-class, attribute selectors</b> (.class, :hover,
            [attr])
          </li>
          <li>
            <b>Element selectors</b> (p, div, h1)
          </li>
          <li>
            <b>Universal selector</b> (*) and inherited styles (lowest)
          </li>
        </ul>
      </div>

      {/* Example 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 1: Element vs Class vs ID</h2>
        <p>
          Here the same element is styled using <b>element</b>, <b>class</b>,
          and <b>ID</b> selectors. The most specific one wins.
        </p>

        <CodeCopyBlock
          code={`p {
  color: blue;        /* element selector */
}
.message {
  color: green;       /* class selector */
}
#special {
  color: red;         /* id selector */
}`}
          language="css"
        />

        <div style={{ marginTop: "1rem" }}>
          <p style={{ color: "blue" }}>Normal paragraph (blue)</p>
          <p className="message" style={{ color: "green" }}>
            Class applied (green overrides blue)
          </p>
          <p id="special" style={{ color: "red" }}>
            ID applied (red overrides all others)
          </p>
        </div>
      </div>

      {/* Example 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 2: Inline Styles</h2>
        <p>
          Inline styles have the <b>highest priority</b>, even over IDs.
        </p>

        <CodeCopyBlock
          code={`#title {
  color: purple;
}
`}
          language="css"
        />

        <div style={{ marginTop: "1rem" }}>
          <h2 id="title" style={{ color: "orange" }}>
            This heading is orange (inline wins over ID)
          </h2>
        </div>
      </div>

      {/* Example 3 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 3: Combining Selectors</h2>
        <p>
          Specificity is calculated as a <b>score</b>: IDs (100) + Classes (10)
          + Elements (1).
        </p>

        <CodeCopyBlock
          code={`/* Scores: */
div p           /* 0-0-2 → 2 */
.content p      /* 0-1-1 → 11 */
#main .content p /* 1-1-1 → 111 */`}
          language="css"
        />

        <div
          style={{
            marginTop: "1rem",
            padding: "10px",
            border: "1px solid #ddd",
          }}
        >
          <p style={{ color: "blue" }}>div p → low specificity</p>
          <p style={{ color: "green" }} className="content">
            .content p → medium specificity
          </p>
          <p style={{ color: "red" }} id="main">
            #main .content p → highest specificity
          </p>
        </div>
      </div>

      {/* Example 4 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 4: !important</h2>
        <p>
          The <code>!important</code> declaration overrides normal specificity,
          but should be avoided unless absolutely necessary.
        </p>

        <CodeCopyBlock
          code={`p {
  color: blue !important;  /* overrides everything */
}
#unique {
  color: red;
}`}
          language="css"
        />

        <div style={{ marginTop: "1rem" }}>
          <p style={{ color: "blue" }}>Blue (forced by !important)</p>
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Always try to keep your selectors <b>simple and meaningful</b>.
          Overusing IDs, inline styles, or <code>!important</code> can make your
          CSS difficult to maintain. Learn to calculate specificity to debug
          conflicts in your stylesheets.
        </p>
      </div>
    </div>
  );
};

export default CSS_Specificity;
